/**
 * Help Tooltip Component
 * Modal-style tooltip for displaying help text on icon click
 * Auto-closes on click outside or ESC key
 */

import React, { useState, useRef, useEffect } from 'react';
import { HelpIcon } from '../Icons/HelpIcon';
import './HelpTooltip.css';

export interface HelpTooltipProps {
  /**
   * Help title/heading
   */
  title: string;

  /**
   * Help text or description
   * Can be string or React node for rich content
   */
  content: React.ReactNode;

  /**
   * Icon color (optional)
   */
  iconColor?: string;

  /**
   * Icon size (optional)
   */
  iconSize?: number;

  /**
   * CSS class
   */
  className?: string;

  /**
   * Position of tooltip relative to icon
   */
  position?: 'top' | 'bottom' | 'left' | 'right';

  /**
   * Show as inline help (always visible)
   */
  inline?: boolean;
}

/**
 * Help Tooltip Component
 * Shows help text in a modal-style popup on icon click
 */
export const HelpTooltip: React.FC<HelpTooltipProps> = ({
  title,
  content,
  iconColor = '#666',
  iconSize = 14,
  className = '',
  position = 'right',
  inline = false,
}) => {
  const [isOpen, setIsOpen] = useState(inline);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate tooltip position when opened
  useEffect(() => {
    if (!isOpen || !containerRef.current) return;

    const calculatePosition = () => {
      const iconElement = containerRef.current?.querySelector('.af-help-icon') as HTMLElement;
      if (!iconElement) return;

      const rect = iconElement.getBoundingClientRect();
      const style: React.CSSProperties = {};

      // Position tooltip based on direction
      if (position === 'left') {
        style.right = `${window.innerWidth - rect.left + 12}px`;
        style.top = `${rect.top + rect.height / 2 - 50}px`; // Center vertically
      } else if (position === 'right') {
        style.left = `${rect.right + 12}px`;
        style.top = `${rect.top + rect.height / 2 - 50}px`;
      } else if (position === 'top') {
        style.left = `${rect.left + rect.width / 2 - 150}px`; // Center horizontally
        style.top = `${rect.top - 12}px`;
        style.transform = 'translateY(-100%)';
      } else if (position === 'bottom') {
        style.left = `${rect.left + rect.width / 2 - 150}px`;
        style.top = `${rect.bottom + 12}px`;
      }

      setTooltipStyle(style);
    };

    calculatePosition();
    window.addEventListener('resize', calculatePosition);
    return () => window.removeEventListener('resize', calculatePosition);
  }, [isOpen, position]);

  // Handle click outside to close
  useEffect(() => {
    if (!isOpen || inline) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, inline]);

  const handleIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!inline) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className={`af-help-tooltip-container ${className}`} ref={containerRef}>
      <HelpIcon
        color={iconColor}
        size={iconSize}
        onClick={handleIconClick}
        ariaLabel={`Help: ${title}`}
      />

      {(isOpen || inline) && (
        <div className={`af-help-tooltip-popup af-help-tooltip-popup--${position}`} style={tooltipStyle}>
          <div className="af-help-tooltip-content">
            <div className="af-help-tooltip-header">
              <h4 className="af-help-tooltip-title">{title}</h4>
              {!inline && (
                <button
                  className="af-help-tooltip-close"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(false);
                  }}
                  type="button"
                  aria-label="Close help"
                >
                  ✕
                </button>
              )}
            </div>
            <div className="af-help-tooltip-body">{content}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpTooltip;
